// src/config/routes.ts

/**
 * Centralized route constants for the application.
 * All navigation paths should reference these constants instead of hardcoded strings.
 */

/**
 * The internal console's prefix (DV-3520: moved from /account, which read like
 * a customer's own settings). Every console path below is built from it, and
 * the side menu derives its console routes from those, so a move changes this
 * one string. Never put "admin" in a console path: the QA and prod WAF 403s any
 * URL path containing it.
 */
export const INTERNAL_CONSOLE_PREFIX = '/internal';

/**
 * The console's old prefix, kept only so old links redirect. The route, the
 * guard row and the mapper all build from this one string; the backlog's
 * "Retire the `/account/*` redirect" entry removes them together.
 */
export const LEGACY_CONSOLE_PREFIX = '/account';

/** Static route paths */
export const ROUTES = {
  // Properties
  PROPERTIES: '/home/properties',
  PROPERTIES_NEW_IMPORT: '/home/properties/new/import',
  PROPERTIES_NEW_FIX_ERROR: '/home/properties/new/fix-error',

  // Orders
  ORDERS: '/home/orders',
  ORDERS_NEW: '/home/orders/new',
  ORDERS_NEW_SINGLE: '/home/orders/new/single',
  ORDERS_NEW_IMPORT: '/home/orders/new/import',
  ORDERS_NEW_FIX_ERROR: '/home/orders/new/fix-error',
  ORDERS_CART: '/home/orders/cart',
  ORDERS_CART_PAYMENT: '/home/orders/cart/payment',
  ORDERS_CONFIRMATION: '/home/orders/confirmation',

  // Top-level orders (outside /home, no auth required)
  ORDERS_NEW_SINGLE_PUBLIC: '/orders/new/single',

  // Integrations
  INTEGRATIONS: '/home/integrations',

  // Team Members
  TEAM_MEMBERS: '/home/team-members',

  // Internal console; see INTERNAL_CONSOLE_PREFIX.
  ACCOUNTS: `${INTERNAL_CONSOLE_PREFIX}/accounts`,
  PROMO_CODES: `${INTERNAL_CONSOLE_PREFIX}/promo-codes`,
  /** The internal cross-account orders list (DV-2848). Back target for the
   *  DV-2849 internal asset route. */
  GLOBAL_ORDERS: `${INTERNAL_CONSOLE_PREFIX}/global-orders`,

  // Other
  COMPANY_LIST: '/home/company-list',
  HELP_FAQ: '/home/help-and-support/faq',
  HELP_CONTACT: '/home/help-and-support/contact'
} as const;

/** DV-2849: the internal asset workspace's base, under the Global Orders list. */
const GLOBAL_ORDER_ASSETS_BASE = `${ROUTES.GLOBAL_ORDERS}/assets` as const;

/**
 * Route pattern strings for <Route path=...> definitions in App.tsx.
 * These are RELATIVE paths because they are used inside nested <Route> elements.
 * For absolute paths used in navigate() calls, use ROUTES or buildRoute instead.
 * The DV-2849 `GLOBAL_ORDER_ASSETS*` and DV-3364 `ACCOUNT_DETAIL` patterns are absolute because they mount at the top level.
 */
export const ROUTE_PATTERNS = {
  PROPERTIES_DETAIL: ':id',
  INTEGRATIONS_VENDOR: 'vendor/:id',
  PROPERTY_ASSETS: ':propertyUUID/assets',
  PROPERTY_ASSETS_PHOTOSET: ':propertyUUID/assets/:photosetUUID',
  /** DV-2849: the internal asset workspace. Declared as siblings of
   *  `/internal/global-orders`, NOT nested under it, so the list page and the
   *  workspace stay independent route elements. The `assets` segment sits
   *  directly after `global-orders` so route-guards.ts can key a row on the
   *  concrete prefix and the admin sidebar's Orders section stays active by
   *  prefix match. Absolute paths — these mount at the top level in App.tsx. */
  GLOBAL_ORDER_ASSETS: `${GLOBAL_ORDER_ASSETS_BASE}/:propertyUUID`,
  GLOBAL_ORDER_ASSETS_PHOTOSET: `${GLOBAL_ORDER_ASSETS_BASE}/:propertyUUID/:photosetUUID`,
  /** Public PhotoPlan gallery: a customer's share link, opened with no session.
   *  Top level, outside /home and /internal, and deliberately NOT under
   *  /assets/ — nginx serves the built bundle from that prefix. */
  PUBLIC_GALLERY: '/gallery/:token',
  /** DV-3364: an account's detail page. `:tab` is an `accountTabs` id; a
   *  missing or unknown one redirects to Overview. An optional view segment
   *  opens a tab's sub-view (DV-3500: `integrations/settings`,
   *  `integrations/direct`); one the tab does not declare redirects to the bare
   *  tab. Absolute: it mounts at the top level, beside the list. */
  ACCOUNT_DETAIL: `${ROUTES.ACCOUNTS}/:entityId/:tab?/:view?`
} as const;

/**
 * An account detail page's tab segment (DV-3364). Kept here, not in the tab
 * registry (`accountTabs`), so the path builder can type it without pulling the
 * lazily loaded tab components into config. A new tab adds its id here.
 */
export type AccountTabId = 'overview' | 'preferences' | 'integrations';
/** A tab's sub-view segment (DV-3500). Each tab declares which it has in `accountTabs`. */
export type AccountTabView = 'settings' | 'direct';
export const DEFAULT_ACCOUNT_TAB: AccountTabId = 'overview';

/**
 * Whether a pathname is the public gallery (`ROUTES.PUBLIC_GALLERY`), compared
 * case-insensitively as React Router matches. For code that runs before the
 * router exists, such as `applyTheme()`.
 */
export const isPublicGalleryPath = (pathname: string): boolean =>
  pathname.toLowerCase().startsWith('/gallery/');

/** Path builder functions for parameterized routes */
export const buildRoute = {
  /** The public PhotoPlan gallery for a photoset's share token. Relative, so
   *  callers prefix window.location.origin to make the link they hand out. */
  publicGallery: (token: string) => `/gallery/${encodeURIComponent(token)}` as const,
  propertyDetail: (id: string) => `/home/properties/${id}` as const,
  integrationVendor: (vendorId: string) =>
    `/home/integrations/vendor/${vendorId}` as const,
  propertyAssets: (
    propertyUUID: string,
    opts?: { photosetUUID?: string; tab?: 'photos' | 'zillow' },
  ) => {
    const base = opts?.photosetUUID
      ? `/home/properties/${propertyUUID}/assets/${opts.photosetUUID}`
      : `/home/properties/${propertyUUID}/assets`;
    return opts?.tab === 'zillow' ? `${base}?tab=zillow` : base;
  },
  /** DV-2849. `view` is the Global Orders tab the editor left, carried so Back
   *  can return to it; search and filters live in sessionStorage and need no
   *  URL help. */
  globalOrderAssets: (
    propertyUUID: string,
    opts?: {
      photosetUUID?: string;
      view?: string;
      orderUUID?: string;
      orderPhotosetUUID?: string;
    },
  ) => {
    const base = opts?.photosetUUID
      ? `${GLOBAL_ORDER_ASSETS_BASE}/${propertyUUID}/${opts.photosetUUID}`
      : `${GLOBAL_ORDER_ASSETS_BASE}/${propertyUUID}`;
    // DV-3521: `orderUUID` is the row the editor was opened from. Uploads are
    // that order's, not the property's latest order's. `orderPhotosetUUID` is
    // that order's own photoset link, so a linked add-on is not warned that the
    // set belongs to another order.
    const query = new URLSearchParams();
    if (opts?.view) query.set('view', opts.view);
    if (opts?.orderUUID) query.set('orderUUID', opts.orderUUID);
    if (opts?.orderPhotosetUUID) query.set('orderPhotosetUUID', opts.orderPhotosetUUID);
    const qs = query.toString();
    return qs ? `${base}?${qs}` : base;
  },
  /** DV-3364. The id is URI-encoded; the tab defaults to Overview. */
  accountDetail: (entityId: string, tab: AccountTabId = DEFAULT_ACCOUNT_TAB, view?: AccountTabView) =>
    `${ROUTES.ACCOUNTS}/${encodeURIComponent(entityId)}/${tab}${view ? `/${view}` : ''}`,
};

const LEGACY_CONSOLE_PREFIX_PATTERN = new RegExp(`^${LEGACY_CONSOLE_PREFIX}(?=/|$)`, 'i');
const LEGACY_ACCOUNT_LIST_SEGMENT = new RegExp(
  `^${INTERNAL_CONSOLE_PREFIX}/account-management(?=/|$)`,
  'i'
);
/**
 * The console routes whose deeper segments are route params: the Global Orders
 * workspace's property and photoset ids, and an account detail page's entity
 * id (DV-3364). Those are compared and sent to the backend verbatim.
 */
const PARAM_BEARING_CONSOLE_PATTERNS = [GLOBAL_ORDER_ASSETS_BASE, ROUTES.ACCOUNTS].map(
  (base) => new RegExp(`^${base}(?=/|$)`, 'i')
);

/**
 * Lowercases the console's fixed segments, so the sidebar's case-sensitive
 * active-route match finds them. Everything below a param-bearing base keeps
 * its case.
 */
const lowercaseConsoleSegments = (path: string): string => {
  const paramBearing = PARAM_BEARING_CONSOLE_PATTERNS.map((pattern) => pattern.exec(path)).find(
    (match) => match !== null
  );
  if (!paramBearing) {
    return path.toLowerCase();
  }
  const fixedLength = paramBearing[0].length;
  return path.slice(0, fixedLength).toLowerCase() + path.slice(fixedLength);
};

/**
 * DV-3520: where an old `/account/...` console URL lives now. Bookmarks and
 * links pasted into tickets keep working through `LegacyConsoleRedirect`.
 * `account-management` became `accounts`; every other segment carries over.
 * Matching is case-insensitive, as React Router's is, so `/Account/Promo-Codes`
 * maps too; the result's fixed segments are always lowercase (see
 * `lowercaseConsoleSegments`). A bare `/account` maps to `/internal`, which
 * redirects on to the user's landing. A path outside `/account` comes back
 * unchanged.
 */
export const internalConsolePathFor = (legacyPathname: string): string => {
  if (!LEGACY_CONSOLE_PREFIX_PATTERN.test(legacyPathname)) {
    return legacyPathname;
  }
  const underInternal = legacyPathname.replace(
    LEGACY_CONSOLE_PREFIX_PATTERN,
    INTERNAL_CONSOLE_PREFIX
  );
  const renamed = underInternal.replace(LEGACY_ACCOUNT_LIST_SEGMENT, ROUTES.ACCOUNTS);
  return lowercaseConsoleSegments(renamed);
};
